/**
 * Client for the host-facing guest tracker (OrchestratorAPI
 * `GroupGuestTrackingController`). Every endpoint is host-gated server-side:
 * 401 without a subject claim, 404 — never 403 — when the caller is not a host,
 * so the API cannot be used to probe which group ids exist. A 404 here
 * therefore means "you are not a host on this trip", never "that group is
 * missing". 403 means a real host attempted an owner-only action.
 *
 * A 409 from send-invite is the exception to the error-envelope shape: it
 * carries a full TrackerInviteResult body rather than `{ error }`, so the
 * conflict message falls back to the fixed copy below.
 *
 * Requests go through the shared orchestrator axios instance, which supplies
 * the bearer token and owns the single 401 recovery path — this module never
 * touches a token.
 */

import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { orchestratorClient } from './groups-api-client';

// ---- contract types (camelCase JSON; the API omits null-valued properties) ----

/** The host-facing RSVP vocabulary. Stored `rsvp_status` uses declined/unknown. */
export type TrackerGoingState = 'going' | 'maybe' | 'cant_go' | 'invited';
export type TrackerBookingSource = 'tripsby' | 'guest';

export const TRACKER_GOING_STATES: readonly TrackerGoingState[] = [
  'going',
  'maybe',
  'cant_go',
  'invited',
];

export const TRACKER_GOING_LABELS: Readonly<Record<TrackerGoingState, string>> = {
  going: 'Going',
  maybe: 'Maybe',
  cant_go: "Can't go",
  invited: 'Invited',
};

export const TRACKER_BOOKING_SOURCE_LABELS: Readonly<Record<TrackerBookingSource, string>> = {
  tripsby: 'Tripsby',
  guest: 'Guest booked',
};

export interface TrackerGuest {
  inviteeId: string;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  rsvpStatus: string;
  rsvpAt?: string | null;
  invitedBy?: string | null;
  plusOneOfInviteeId?: string | null;
  goingOverride?: string | null;
  arrivalDate?: string | null;
  departureDate?: string | null;
  stayingAt?: string | null;
  roomBlockId?: string | null;
  bookingSource?: string | null;
  partySize?: number | null;
  /** Room number + confirmation number. Host-only — never rendered outside this page. */
  roomDetails?: string | null;
  preferencesNotes?: string | null;
  customFields?: Record<string, Record<string, string>> | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
  going: string;
  dinners: number;
  /**
   * Tag ids, not labels — the vocabulary rides on the board once, in
   * {@link TrackerBoard.tags}. 200 guests × 5 tags × (label + colour + kind) is
   * payload for nothing, and a rename would have to rewrite every row. Same
   * split the board already makes between `sections` and `customFields`.
   *
   * Optional because Studio and the API deploy separately: an API that predates
   * tags omits the key entirely, which is the capability probe the UI reads (see
   * `tagsAvailable` in lib/guestTags.ts). It must not be given a default here.
   */
  tagIds?: string[];
  /** Per-assignment detail — "EpiPen in her bag" — keyed by tag id. Host-only. */
  tagNotes?: Record<string, string>;
}

/**
 * One tag in a trip's vocabulary.
 *
 * `slug` is the canonical name and the thing uniqueness is enforced on;
 * `label` is what the host typed. `isCritical` is CHECK-constrained to the
 * dietary and access kinds server-side, so nobody can flag "VIP" critical and
 * land it on the kitchen sheet.
 */
export interface GuestTag {
  id: string;
  slug: string;
  label: string;
  kind: string;
  /** `#rrggbb`, or null for "derive one from the slug". */
  color?: string | null;
  isCritical: boolean;
  position: number;
}

/**
 * A bond between exactly two guests: what they are, and what the seater must do
 * about it.
 *
 * `relation` and `seating` are separate columns because "they're cousins, keep
 * them apart" is one honest fact and a single `kind` column would force it into
 * two contradictory ones. The server canonicalizes the pair ordering, so a
 * caller never has to know which id goes first.
 */
export interface GuestLink {
  id: string;
  inviteeId: string;
  otherId: string;
  relation: string;
  seating: string;
  strength: string;
  note?: string | null;
}

export interface TrackerSection {
  id: string;
  sectionKey: string;
  label: string;
  position: number;
}

export interface TrackerHost {
  id: string;
  email: string;
  displayName?: string | null;
  addedBy?: string | null;
  createdAt: string;
}

export interface TrackerSummary {
  rsvps: { going: number; maybe: number; cantGo: number; invited: number; total: number };
  byHotel: { stayingAt: string; count: number; guests: string[] }[];
  bookedVia: { tripsby: number; guest: number; unknown: number };
}

export interface TrackerBoard {
  guests: TrackerGuest[];
  sections: TrackerSection[];
  summary: TrackerSummary;
  hosts: TrackerHost[];
  /**
   * The tag vocabulary, sent once for the whole board.
   *
   * Optional, and deliberately so: its ABSENCE is how Studio knows the API has
   * no tag routes yet, and every tag control is hidden rather than rendered
   * doing nothing. An empty array means tags exist and this trip has none —
   * a different fact, and the difference is the whole point.
   */
  tags?: GuestTag[];
  links?: GuestLink[];
}

/**
 * A partial update. An omitted key preserves the stored value; an explicit
 * `null` clears it — the server models the same distinction with PatchValue,
 * so `undefined` must never be serialized into the body.
 */
export interface TrackerPatch {
  going?: TrackerGoingState | null;
  arrivalDate?: string | null;
  departureDate?: string | null;
  stayingAt?: string | null;
  roomBlockId?: string | null;
  bookingSource?: TrackerBookingSource | null;
  partySize?: number | null;
  roomDetails?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  preferencesNotes?: string | null;
  customFields?: Record<string, Record<string, string | null> | null> | null;
}

export interface TrackerImportRow {
  /**
   * The guest this row came from, when the file is a Tripsby export being sent
   * back. It makes the round trip exact: a host exports the roster, types the
   * missing emails into a spreadsheet and imports it, and every row lands on the
   * guest it came from instead of arriving as a new person. Null for any other
   * file, which matches on email and then name as before.
   */
  inviteeId?: string | null;
  name: string;
  email?: string | null;
  /** Counts toward reachability exactly as an email does. */
  phone?: string | null;
  status?: string | null;
  rsvpAt?: string | null;
  invitedBy?: string | null;
  plusOneOf?: string | null;
  /**
   * The three fields the mapper detected and then dropped on the floor.
   *
   * `columnMapping.ts` has always detected a notes column, shown the host it was
   * mapped, and built a row object with no `notes` key — so the import confirmed
   * a column it never read. Tags and dietary arrive as slugs the server
   * auto-creates; the host is told how many new tags that will be before
   * confirming.
   */
  notes?: string | null;
  tagSlugs?: string[];
  dietarySlugs?: string[];
}

export interface TrackerImportResult {
  created: number;
  updated: number;
  linkedPlusOnes: number;
  errors: { row: number; name: string; error: string }[];
}

export interface TrackerInviteResult {
  inviteeId: string;
  fullName: string;
  email?: string | null;
  status: string;
  error?: string | null;
}

// ---- request plumbing ----

export type TrackerFailure =
  | { kind: 'unauthorized' }
  | { kind: 'forbidden'; message: string }
  | { kind: 'not_host' }
  | { kind: 'invalid'; message: string }
  | { kind: 'conflict'; message: string }
  | { kind: 'error'; message: string };

export type TrackerResult<T> = { ok: true; data: T } | { ok: false; failure: TrackerFailure };

/**
 * What to tell the host about a failed tracker call. Lives beside the
 * `TrackerFailure` union so a new `kind` and its wording are one edit, and
 * the exhaustive switch fails to compile until every caller has the message.
 */
export function trackerFailureMessage(failure: TrackerFailure): string {
  switch (failure.kind) {
    case 'unauthorized':
      return 'Your session expired. Sign in again.';
    case 'not_host':
      return "You're not a host on this trip.";
    case 'forbidden':
    case 'invalid':
    case 'conflict':
    case 'error':
      return failure.message;
  }
}

const GENERIC_ERROR = 'Something went wrong. Try again.';

function errorMessage(data: unknown, fallback: string): string {
  const body = data as { error?: unknown } | undefined;
  return typeof body?.error === 'string' && body.error.trim() ? body.error : fallback;
}

/**
 * The export asks axios for a Blob, and axios applies `responseType` to the
 * error body too — so a failed export arrives as a Blob of JSON and the server's
 * own explanation reads as undefined. Recovering it is the difference between
 * telling a host why their download was refused and telling them "try again".
 */
async function readErrorBody(error: unknown): Promise<unknown> {
  if (!axios.isAxiosError(error)) return undefined;
  const data = error.response?.data;
  if (!(data instanceof Blob)) return data;
  try {
    return JSON.parse(await data.text());
  } catch {
    return undefined;
  }
}

export function toTrackerFailure(error: unknown, errorBody?: unknown): TrackerFailure {
  if (!axios.isAxiosError(error)) return { kind: 'error', message: GENERIC_ERROR };
  if (!error.response) {
    // A client-side abort is not a network failure, and saying "couldn't reach
    // the server" about work that may well have committed invites a re-run.
    if (error.code === 'ECONNABORTED') {
      return {
        kind: 'error',
        message: 'That took too long to come back. Reload before trying again — it may have gone through.',
      };
    }
    return { kind: 'error', message: "Couldn't reach the server." };
  }

  const { status } = error.response;
  const data = errorBody ?? error.response.data;
  if (status === 401) return { kind: 'unauthorized' };
  // The gate answers 404 for "not a host" so it cannot confirm a group exists.
  if (status === 404) return { kind: 'not_host' };
  if (status === 403) {
    return { kind: 'forbidden', message: errorMessage(data, 'Only the owner can do that.') };
  }
  if (status === 400) {
    return { kind: 'invalid', message: errorMessage(data, "That value wasn't accepted.") };
  }
  if (status === 409) {
    return {
      kind: 'conflict',
      message: errorMessage(data, 'That change conflicts with another guest.'),
    };
  }
  return { kind: 'error', message: errorMessage(data, GENERIC_ERROR) };
}

/**
 * The shared client's 30s timeout suits a single-row PATCH and is wrong for the
 * two bulk endpoints: a whole-roster import upserts every row and links the
 * plus-ones, and the export builds the file server-side. Aborting those is not
 * a harmless retry — the work has already landed, the host is told it failed,
 * and importing again is how a roster grows duplicates.
 */
const BULK_TIMEOUT_MS = 180_000;

async function request<T>(path: string, config: AxiosRequestConfig = {}): Promise<TrackerResult<T>> {
  try {
    const { data } = await orchestratorClient.request<T>({
      url: path,
      headers: { Accept: 'application/json' },
      ...config,
    });
    return { ok: true, data };
  } catch (error) {
    const body = await readErrorBody(error);
    return { ok: false, failure: toTrackerFailure(error, body) };
  }
}

const groupPath = (groupId: string) => `/api/groups/${encodeURIComponent(groupId)}`;

// ---- endpoints ----

export function fetchTrackerBoard(groupId: string): Promise<TrackerResult<TrackerBoard>> {
  return request<TrackerBoard>(`${groupPath(groupId)}/tracker`);
}

export function patchTrackerGuest(
  groupId: string,
  inviteeId: string,
  patch: TrackerPatch,
): Promise<TrackerResult<TrackerGuest>> {
  // The patch object goes straight through: JSON.stringify keeps explicit
  // nulls (clear the field) and drops undefined (leave it alone). Nothing may
  // sit in between that strips nullish values.
  return request<TrackerGuest>(`${groupPath(groupId)}/tracker/${encodeURIComponent(inviteeId)}`, {
    method: 'PATCH',
    data: patch,
  });
}

export function importTrackerRows(
  groupId: string,
  rows: TrackerImportRow[],
): Promise<TrackerResult<TrackerImportResult>> {
  return request<TrackerImportResult>(`${groupPath(groupId)}/tracker/import`, {
    method: 'POST',
    data: { rows },
    timeout: BULK_TIMEOUT_MS,
  });
}

/** The export carries contact details and confirmation numbers — host download only. */
export function fetchTrackerExport(groupId: string): Promise<TrackerResult<Blob>> {
  return request<Blob>(`${groupPath(groupId)}/tracker/export`, {
    responseType: 'blob',
    headers: { Accept: 'text/csv' },
    timeout: BULK_TIMEOUT_MS,
  });
}

/** The host's note and signature for one send. Both optional — the server falls
 * back to its default note and the hosts' display names. */
export interface InviteCompose {
  message?: string;
  signedBy?: string;
}

export function sendGuestInvite(
  groupId: string,
  inviteeId: string,
  compose?: InviteCompose,
): Promise<TrackerResult<TrackerInviteResult>> {
  return request<TrackerInviteResult>(
    `${groupPath(groupId)}/tracker/${encodeURIComponent(inviteeId)}/send-invite`,
    compose ? { method: 'POST', data: compose } : { method: 'POST' },
  );
}

export function createTrackerSection(
  groupId: string,
  label: string,
): Promise<TrackerResult<TrackerSection>> {
  return request<TrackerSection>(`${groupPath(groupId)}/tracker/sections`, {
    method: 'POST',
    data: { label },
  });
}

export async function deleteTrackerSection(
  groupId: string,
  sectionId: string,
): Promise<TrackerResult<null>> {
  // 204 — axios hands back an empty string, so normalize it to the null the
  // callers expect.
  const result = await request<unknown>(
    `${groupPath(groupId)}/tracker/sections/${encodeURIComponent(sectionId)}`,
    { method: 'DELETE' },
  );
  return result.ok ? { ok: true, data: null } : result;
}

// ---- collaborative hosts ----

/**
 * Everyone who can manage this trip.
 *
 * Reading is host-gated; adding and removing are OWNER-only and answer 403 with
 * a sentence — a co-host cannot quietly widen the circle that reads guests'
 * phone numbers, home airports and confirmation numbers. `toTrackerFailure`
 * already turns that into `{ kind: 'forbidden' }`, which is what lets the panel
 * show the list to a co-host and the controls to nobody but the owner.
 *
 * The board has always carried `hosts` — it is what signs an invite email. What
 * was missing was any way to change it: a second host had to be added by
 * someone with database access, on a product whose whole premise is that a
 * couple plans the weekend together.
 */
export function listTrackerHosts(groupId: string): Promise<TrackerResult<TrackerHost[]>> {
  return request<TrackerHost[]>(`${groupPath(groupId)}/hosts`);
}

export function addTrackerHost(
  groupId: string,
  email: string,
  displayName?: string,
): Promise<TrackerResult<TrackerHost>> {
  const trimmedName = displayName?.trim();
  return request<TrackerHost>(`${groupPath(groupId)}/hosts`, {
    method: 'POST',
    // The name is optional and the server falls back to the email, so an empty
    // box must send nothing rather than an empty string that becomes the label
    // on every invite this person signs.
    data: trimmedName ? { email: email.trim(), displayName: trimmedName } : { email: email.trim() },
  });
}

export async function removeTrackerHost(
  groupId: string,
  hostId: string,
): Promise<TrackerResult<null>> {
  // 204 — axios hands back an empty string, so normalize it to the null the
  // callers expect.
  const result = await request<unknown>(
    `${groupPath(groupId)}/hosts/${encodeURIComponent(hostId)}`,
    { method: 'DELETE' },
  );
  return result.ok ? { ok: true, data: null } : result;
}
